interface ZipFile {
  url: string;
  filename: string;
}

interface ZipHeaders {
  [key: string]: string;
}

interface ZipStatus {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  total: number;
  processed: number;
  failed?: string[];
  message?: string;
}

interface ZipDownloadResult {
  blob: Blob;
  failed: string[];
}

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 300; // 300 * 2s = 10 minutes

/**
 * Builds a ZIP archive from multiple file URLs via the async media-bank/download-zip
 * flow: enqueue -> poll status until completed/failed -> download the finished archive.
 */
export default function useZipDownload() {
  const runtimeConfig = useRuntimeConfig();

  const enqueue = async(files: ZipFile[], headers: ZipHeaders): Promise<string> => {
    const response = await $fetch<{ id: string }>(`${runtimeConfig.public.apiUrl}/media-bank/download-zip`, {
      method: 'POST',
      body: { files },
      headers,
    });

    return response.id;
  };

  const pollStatus = async(id: string, headers: ZipHeaders): Promise<ZipStatus> => {
    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
      const status = await $fetch<ZipStatus>(
        `${runtimeConfig.public.apiUrl}/media-bank/download-zip/${id}/status`,
        { headers }
      );

      if (status.status === 'completed' || status.status === 'failed') {
        return status;
      }

      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
    }

    throw new Error('Timed out waiting for the ZIP to finish building.');
  };

  const downloadZipBatch = async(files: ZipFile[], headers: ZipHeaders): Promise<ZipDownloadResult> => {
    const id = await enqueue(files, headers);
    const status = await pollStatus(id, headers);

    if (status.status !== 'completed') {
      throw new Error(status.message || 'Media bank ZIP build failed');
    }

    const blob = await $fetch<Blob>(`${runtimeConfig.public.apiUrl}/media-bank/download-zip/${id}/download`, {
      responseType: 'blob',
      headers,
    });

    return { blob, failed: status.failed ?? [] };
  };

  return { downloadZipBatch };
}
